import { createColumnHelper } from '@tanstack/react-table';
import { IMergedHeadersReturn } from './index.headers';
import { CellTypes, renderCell } from '../../helpers/cells.helper';
import { IInventory } from '../../models/inventory.model';
import { usePageSettings } from '@monorepo/tools/src/lib/hooks/tools/use-page-settings';
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AccountTypeEnum, IAccount } from '../../models/account.model';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { IMFPPStore } from '../../stores';
import { PrimaryButton, ButtonDimensions, Directions } from '@monorepo/base/src/components/buttons/buttons';
import { Alignments, Tooltip } from '@monorepo/controlled/src/components/tooltip/tooltip/tooltip';

const columnHelper = createColumnHelper<IInventory>();

/** TanStack Table column `size` / `minSize` / `maxSize` are in CSS pixels for the default layout. */
const ACTION_COLUMN_SIZE_PX = 172;
const ACTION_COLUMN_MIN_PX = 148;
const ACTION_COLUMN_MAX_PX = 200;
const GENERATE_LINK_BUTTON_WIDTH = '158px';

interface IUseInventoryHeadersOptions {
	onGenerateLink?: (inventory: IInventory) => void;
}

export const useInventoryHeaders = (options?: IUseInventoryHeadersOptions): IMergedHeadersReturn<IInventory> => {
	const { onGenerateLink } = options || {};
	const { tableStore } = usePageSettings();
	const [searchParams] = useSearchParams();
	const { publisherStore } = useStores<IMFPPStore>();
	const accountList = publisherStore.getListStore().getData();
	const accountIdParam = searchParams.get('accountId') ?? undefined;

	// Derive from current list so allowSelfLinkGeneration updates when publishers are refetched (e.g. after Manifesto changes).
	const accountSelected = useMemo((): IAccount | null => {
		if (!accountIdParam || !Array.isArray(accountList) || !accountList.length) {
			return null;
		}
		return accountList.find(account => `${account?.id}` === accountIdParam) ?? null;
	}, [accountIdParam, accountList]);

	const canGenerate = accountSelected?.allowSelfLinkGeneration === true;

	useEffect(() => {
		if (!tableStore) {
			return;
		}
		const colVis = {
			id: false,
			domain: true,
			geo: true,
			deepLink: true,
			vertical: false,
			commission: accountSelected?.type === AccountTypeEnum.CPA,
			cpc: accountSelected?.type === AccountTypeEnum.CPC,
			clickCap: false,
			action: true,
		};
		tableStore.setDefaultColumnVisibility(colVis);
		const prev = tableStore.getColumnVisibility() || {};
		const hasUserVisibility = Object.keys(prev).length > 0;
		if (!hasUserVisibility) {
			tableStore.setColumnVisibility(colVis);
		} else {
			tableStore.setColumnVisibility({
				...prev,
				id: colVis.id,
				commission: colVis.commission,
				cpc: colVis.cpc,
			});
		}
	}, [tableStore, accountIdParam, accountSelected?.type]);

	const setColumns = () => {
		const columns = [
			columnHelper.accessor('id', {
				header: 'ID',
			}),
			columnHelper.accessor('domain', {
				header: 'Domain',
				cell: ({ row }) => {
					return <span>{renderCell(CellTypes.Domain, row, 'domain')}</span>;
				},
			}),
			columnHelper.accessor('geo', {
				header: 'Geo',
				cell: ({ row }) => {
					return <span>{renderCell(CellTypes.Geo, row, 'geo')}</span>;
				},
			}),
			columnHelper.accessor('deepLink', {
				header: 'Deep Link',
				cell: ({ row }) => {
					return <span>{renderCell(CellTypes.DL, row, 'deepLink')}</span>;
				},
			}),
			columnHelper.accessor('vertical', {
				header: 'Vertical',
				cell: ({ row }) => {
					return <span>{renderCell(CellTypes.Text, row, 'vertical')}</span>;
				},
			}),
			columnHelper.accessor('commission', {
				header: 'Commission',
				cell: ({ row }) => {
					return accountSelected?.type !== AccountTypeEnum.CPA ? null : (
						<span>{renderCell(CellTypes.Text, row, 'commission')}</span>
					);
				},
			}),
			columnHelper.accessor('cpc', {
				header: 'CPC',
				cell: ({ row }) => {
					return accountSelected?.type !== AccountTypeEnum.CPC ? null : (
						<span>{renderCell(CellTypes.Currency, row, 'cpc', { decimalLen: 5 })}</span>
					);
				},
			}),
			// columnHelper.accessor('link', {
			// 	header: 'Link',
			// 	cell: ({ row }) => {
			// 		return <span>{renderCell(CellTypes.Link, row, 'link')}</span>;
			// 	},
			// }),
			columnHelper.accessor('clickCap', {
				header: 'Click Cap',
				cell: ({ row }) => {
					return <span>{row.getValue('clickCap') == 0 ? '--' : row.getValue('clickCap')}</span>;
				},
			}),
			columnHelper.display({
				id: 'action',
				header: 'Action',
				size: ACTION_COLUMN_SIZE_PX,
				minSize: ACTION_COLUMN_MIN_PX,
				maxSize: ACTION_COLUMN_MAX_PX,
				cell: ({ row }) => {
					const btn = (
						<PrimaryButton
							dimension={ButtonDimensions.Medium}
							width={GENERATE_LINK_BUTTON_WIDTH}
							disabled={!canGenerate}
							onClick={() => canGenerate && onGenerateLink?.(row.original)}
							icon="link-01"
							iconSize="16px"
							iconDirection={Directions.FromLeft}>
							Generate Link
						</PrimaryButton>
					);
					if (!canGenerate) {
						return (
							<Tooltip
								align={Alignments.FromRight}
								content={'Link generation is not available. Please contact your Account Manager.'}>
								<span>{btn}</span>
							</Tooltip>
						);
					}
					return btn;
				},
			}),
		];
		return columns;
	};

	return {
		columns: setColumns(),
	};
};
